// PlotPanel.js - Frequency Sweep Plotting Component

import React, { useState } from 'react';
import styled from 'styled-components';
import Plot from './PlotlyChart';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const PlotPanel = ({ onClose }) => {
  const { sweepResults, isSweeping } = useStore();
  const [activeTab, setActiveTab] = useState('gain');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 800, height: 500 });

  // Calculate default position: 50px from lower-right corner
  const getDefaultPosition = () => ({
    x: window.innerWidth - size.width - 50,
    y: window.innerHeight - size.height - 50
  });
  const [position, setPosition] = useState(getDefaultPosition);

  // Calculate actual common frequency range from sweep data (where data is valid)
  const getValidFrequencyRange = () => {
    if (!sweepResults || !sweepResults.frequencies || !sweepResults.gain) {
      return { start: null, stop: null };
    }
    // Find indices where gain is not null (valid data)
    const validIndices = sweepResults.gain
      .map((g, i) => g !== null ? i : -1)
      .filter(i => i !== -1);

    if (validIndices.length === 0) {
      return { start: null, stop: null };
    }

    const startFreq = sweepResults.frequencies[validIndices[0]];
    const stopFreq = sweepResults.frequencies[validIndices[validIndices.length - 1]];
    return { start: Math.round(startFreq), stop: Math.round(stopFreq) };
  };

  const validRange = getValidFrequencyRange();

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleDrag = React.useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleDragEnd = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  if (!sweepResults) {
    return (
      <Container style={{ left: position.x, top: position.y, width: 400, height: 200 }}>
        <Header
          onMouseDown={(e) => handleDragStart(e)}
        >
          <Title>Frequency Sweep Plots</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        <ContentArea>
          <EmptyMessage>No sweep results available. Run a frequency sweep first.</EmptyMessage>
        </ContentArea>
      </Container>
    );
  }

  // Common plot layout configuration
  const plotLayout = {
    autosize: true,
    margin: { l: 60, r: 40, t: 40, b: 60 },
    paper_bgcolor: THEME.panelBackground,
    plot_bgcolor: THEME.cardBackground,
    font: {
      color: THEME.textPrimary,
      family: 'Inter, system-ui, sans-serif',
      size: 12
    },
    xaxis: {
      title: { text: 'Frequency (MHz)', font: { size: 14 } },
      gridcolor: '#404040',
      showgrid: true,
      zeroline: false,
      color: THEME.textSecondary,
      range: validRange.start !== null && validRange.stop !== null
        ? [validRange.start, validRange.stop]
        : undefined
    },
    yaxis: {
      gridcolor: '#404040',
      showgrid: true,
      zeroline: true,
      zerolinecolor: '#606060',
      color: THEME.textSecondary
    },
    hovermode: 'x unified',
    showlegend: true,
    legend: {
      x: 1.02,
      y: 1,
      xanchor: 'left',
      bgcolor: 'rgba(30, 30, 30, 0.8)',
      bordercolor: '#404040',
      borderwidth: 1
    }
  };

  // Common plot config
  const plotConfig = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'autoScale2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: `gchain_${activeTab}_plot`,
      height: 1080,
      width: 1920,
      scale: 2
    }
  };

  // Render different plots based on active tab
  const renderPlot = () => {
    switch (activeTab) {
      case 'gain':
        return (
          <PlotWrapper>
            {isSweeping && (
              <LoadingOverlay>
                <LoadingSpinner />
                <LoadingText>Calculating...</LoadingText>
              </LoadingOverlay>
            )}
            <Plot
              data={[
                {
                  x: sweepResults.frequencies,
                  y: sweepResults.gain,
                  type: 'scatter',
                  mode: 'lines',
                  name: 'System Gain',
                  line: {
                    color: THEME.accentPrimary,
                    width: 2
                  },
                  hovertemplate: '<b>%{y:.2f} dB</b><br>@ %{x:.1f} MHz<extra></extra>'
                }
              ]}
              layout={{
                ...plotLayout,
                title: { text: 'System Gain vs Frequency', font: { size: 16 } },
                yaxis: {
                  ...plotLayout.yaxis,
                  title: { text: 'Gain (dB)', font: { size: 14 } }
                }
              }}
              config={plotConfig}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          </PlotWrapper>
        );

      case 'noiseFigure':
        return (
          <Plot
            data={[
              {
                x: sweepResults.frequencies,
                y: sweepResults.noiseFigure,
                type: 'scatter',
                mode: 'lines',
                name: 'Noise Figure',
                line: {
                  color: THEME.info,
                  width: 2
                },
                hovertemplate: '<b>%{y:.2f} dB</b><br>@ %{x:.1f} MHz<extra></extra>'
              }
            ]}
            layout={{
              ...plotLayout,
              title: { text: 'Noise Figure vs Frequency', font: { size: 16 } },
              yaxis: {
                ...plotLayout.yaxis,
                title: { text: 'Noise Figure (dB)', font: { size: 14 } }
              }
            }}
            config={plotConfig}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        );

      case 'outputPower':
        return (
          <Plot
            data={[
              {
                x: sweepResults.frequencies,
                y: sweepResults.outputPower,
                type: 'scatter',
                mode: 'lines',
                name: 'Output Power',
                line: {
                  color: '#EC4899',
                  width: 2
                },
                hovertemplate: '<b>%{y:.2f} dBm</b><br>@ %{x:.1f} MHz<extra></extra>'
              }
            ]}
            layout={{
              ...plotLayout,
              title: { text: 'Output Power vs Frequency', font: { size: 16 } },
              yaxis: {
                ...plotLayout.yaxis,
                title: { text: 'Output Power (dBm)', font: { size: 14 } }
              }
            }}
            config={plotConfig}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        );

      case 'p1db':
        return (
          <Plot
            data={[
              {
                x: sweepResults.frequencies,
                y: sweepResults.p1db,
                type: 'scatter',
                mode: 'lines',
                name: 'OP1dB',
                line: {
                  color: '#F59E0B',
                  width: 2
                },
                hovertemplate: '<b>%{y:.2f} dBm</b><br>@ %{x:.1f} MHz<extra></extra>'
              }
            ]}
            layout={{
              ...plotLayout,
              title: { text: 'Output 1dB Compression Point vs Frequency', font: { size: 16 } },
              yaxis: {
                ...plotLayout.yaxis,
                title: { text: 'OP1dB (dBm)', font: { size: 14 } }
              }
            }}
            config={plotConfig}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        );

      case 'oip3':
        return (
          <Plot
            data={[
              {
                x: sweepResults.frequencies,
                y: sweepResults.oip3,
                type: 'scatter',
                mode: 'lines',
                name: 'OIP3',
                line: {
                  color: '#8B5CF6',
                  width: 2
                },
                hovertemplate: '<b>%{y:.2f} dBm</b><br>@ %{x:.1f} MHz<extra></extra>'
              }
            ]}
            layout={{
              ...plotLayout,
              title: { text: 'Output Third-Order Intercept Point vs Frequency', font: { size: 16 } },
              yaxis: {
                ...plotLayout.yaxis,
                title: { text: 'OIP3 (dBm)', font: { size: 14 } }
              }
            }}
            config={plotConfig}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        );

      default:
        return <EmptyMessage>Select a plot type</EmptyMessage>;
    }
  };

  return (
    <Container
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height
      }}
    >
      <Header
        onMouseDown={handleDragStart}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <Title>📊 Frequency Sweep Plots</Title>
        <HeaderRight>
          <FreqRange>
            {validRange.start !== null && validRange.stop !== null
              ? `${validRange.start} - ${validRange.stop} MHz`
              : 'No valid data'}
          </FreqRange>
          <CloseButton onClick={onClose}>×</CloseButton>
        </HeaderRight>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'gain'} onClick={() => setActiveTab('gain')}>
          Gain
        </Tab>
        <Tab active={activeTab === 'noiseFigure'} onClick={() => setActiveTab('noiseFigure')}>
          Noise Figure
        </Tab>
        <Tab active={activeTab === 'p1db'} onClick={() => setActiveTab('p1db')}>
          OP1dB
        </Tab>
        <Tab active={activeTab === 'oip3'} onClick={() => setActiveTab('oip3')}>
          OIP3
        </Tab>
        <Tab active={activeTab === 'outputPower'} onClick={() => setActiveTab('outputPower')}>
          Output Power
        </Tab>
      </TabContainer>

      <PlotArea>
        {renderPlot()}
      </PlotArea>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  background: ${THEME.panelBackground};
  border: 1px solid #404040;
  border-radius: 8px;
  box-shadow: ${THEME.shadowLarge};
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.md};
  background: ${THEME.sidebarBackground};
  border-bottom: 1px solid #404040;
  user-select: none;
`;

const Title = styled.h3`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.lg};
  font-weight: 600;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.md};
`;

const FreqRange = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
  font-family: 'Courier New', monospace;
`;

const CloseButton = styled.button`
  background: transparent;
  color: ${THEME.textSecondary};
  font-size: 28px;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: ${THEME.borderRadiusSmall};
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(220, 38, 38, 0.2);
    color: ${THEME.accentPrimary};
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: ${THEME.sidebarBackground};
  border-bottom: 1px solid #404040;
  overflow-x: auto;
`;

const Tab = styled.button`
  background: ${props => props.active ? THEME.panelBackground : 'transparent'};
  color: ${props => props.active ? THEME.accentPrimary : THEME.textSecondary};
  border: none;
  border-bottom: 2px solid ${props => props.active ? THEME.accentPrimary : 'transparent'};
  padding: ${THEME.spacing.sm} ${THEME.spacing.lg};
  font-size: ${THEME.fontSize.md};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  white-space: nowrap;

  &:hover {
    background: rgba(220, 38, 38, 0.1);
    color: ${THEME.textPrimary};
  }
`;

const PlotArea = styled.div`
  flex: 1;
  display: flex;
  padding: ${THEME.spacing.md};
  overflow: hidden;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${THEME.spacing.lg};
`;

const EmptyMessage = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.md};
  text-align: center;
`;

const PlotWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 4px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${THEME.textMuted};
  border-top-color: ${THEME.accentPrimary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  margin-top: ${THEME.spacing.md};
`;

export default PlotPanel;
